import { ApiDomain } from "@/shared/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { UserRole } from "../types";

type PreviouwRole = UserRole | null;
type PreviousPreviewRole = UserRole | null;
type ChangeCallback = (previewRole: PreviouwRole, previousPreviewRole: PreviousPreviewRole, apiDomain: ApiDomain | null) => void;

const usePreviewRoleChange = (apiDomain: ApiDomain | null, previewRole: UserRole | null, callback: ChangeCallback): void => {
    const isInitialMount = useRef(true);
    const callbackRef = useRef(callback);

    useEffect(() => {
        // update callback ref if it changed
        callbackRef.current = callback;
    }, [callback]);

    const prevPreviewRoleRef = useRef<UserRole | null>(null);

    useEffect(() => {
        // Skip initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevPreviewRoleRef.current = previewRole;
            return;
        }

        const prevRole = prevPreviewRoleRef.current;
        // call change callback to act on the change
        callbackRef.current(previewRole, prevRole, apiDomain);
        // Update ref for next comparison
        prevPreviewRoleRef.current = previewRole;
    }, [previewRole, apiDomain]);
};

/**
 * Invalidates or removes queries when the preview role changes.
 * Uses apiDomain to determine the invalidation strategy:
 * - If switching to a valid domain: invalidate and reset to trigger refetch
 * - If switching to null domain: remove queries without refetching
 *
 * Skips the initial mount to avoid unnecessary invalidation.
 */
export function useInvalidateQueriesOnDomainChange(apiDomain: ApiDomain | null, previewRole: UserRole | null) {
    const queryClient = useQueryClient();

    const invalidateQueries = useCallback<ChangeCallback>(
        (previewRole, previousPreviewRole, apiDomain) => {
            // Only invalidate if preview role actually changed
            if (previousPreviewRole !== previewRole) {
                console.debug(`Preview role changed from ${previousPreviewRole} to ${previewRole}`);

                if (apiDomain !== null) {
                    // Switching to a valid domain - invalidate and reset to trigger refetch
                    console.debug("Invalidating all queries to refetch with new domain");
                    void queryClient.invalidateQueries();
                    void queryClient.resetQueries();
                } else {
                    // Switching to null domain - remove queries without refetching
                    console.debug("Removing all queries (no refetch)");
                    queryClient.removeQueries();
                }
            }
        },
        [queryClient]
    );

    usePreviewRoleChange(apiDomain, previewRole, invalidateQueries);
}

export function useCancelSubscriptionsOnDomainChange(
    apiDomain: ApiDomain | null,
    previewRole: UserRole | null,
    cancelSubscriptions: () => void
): void {
    const callback = useCallback<ChangeCallback>(
        (previewRole, previousPreviewRole) => {
            // Only invalidate if preview role actually changed
            if (previousPreviewRole !== previewRole) {
                console.debug(`Preview role changed from ${previousPreviewRole} to ${previewRole}`);
                cancelSubscriptions();
            }
        },
        [cancelSubscriptions]
    );

    usePreviewRoleChange(apiDomain, previewRole, callback);
}
